export function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ type: "ul", items: list }); list = null; } };
  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx];
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) {
      list = list || [];
      list.push(line.replace(/^\s*[-*]\s+/, ""));
      continue;
    }
    flush();
    if (!line.trim()) continue;
    // Markdown table: header row | separator row | one or more body rows
    if (/^\s*\|/.test(line) && idx + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[idx + 1])) {
      const parseRow = (r) => r.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const header = parseRow(line);
      idx += 2; // skip separator
      const rows = [];
      while (idx < lines.length && /^\s*\|/.test(lines[idx])) {
        rows.push(parseRow(lines[idx]));
        idx++;
      }
      idx--; // outer loop will re-increment
      blocks.push({ type: "table", header, rows });
      continue;
    }
    const yt = line.match(/^https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})\S*$/);
    if (yt) { blocks.push({ type: "youtube", id: yt[1] }); continue; }
    const img = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) blocks.push({ type: "img", alt: img[1], src: img[2] });
    else if (line.startsWith("### ")) blocks.push({ type: "h3", text: line.slice(4) });
    else if (line.startsWith("## ")) blocks.push({ type: "h2", text: line.slice(3) });
    else if (line.startsWith("# ")) blocks.push({ type: "h2", text: line.slice(2) });
    else blocks.push({ type: "p", text: line });
  }
  flush();
  return blocks;
}

const inline = (text, key) =>
  text.split(/(\[[^\]]+\]\([^)]+\)|\/[a-z0-9][a-z0-9-]{2,}|\*\*[^*]+\*\*)/g).map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link)
      return <a key={`${key}-${i}`} href={link[2]} className="text-emerald-700 font-semibold hover:underline"
        {...(link[2].startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{link[1]}</a>;
    if (/^\/[a-z0-9][a-z0-9-]{2,}$/.test(part))
      return <span key={`${key}-${i}`} className="cmd-chip bg-slate-900 text-amber-400 font-bold px-1.5 py-0.5 rounded text-[0.85em]">{part}</span>;
    if (/^\*\*[^*]+\*\*$/.test(part))
      return <strong key={`${key}-${i}`}>{part.slice(2, -2)}</strong>;
    return part;
  });

export const MarkdownView = ({ content }) => (
  <div className="space-y-4">
    {renderMarkdown(content).map((b, i) => {
      if (b.type === "youtube") return (
        <div key={i} className="relative aspect-video rounded-2xl overflow-hidden my-4 bg-black" data-testid="blog-youtube-embed">
          <iframe src={`https://www.youtube.com/embed/${b.id}`} title="YouTube video"
            className="absolute inset-0 w-full h-full" loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
        </div>
      );
      if (b.type === "img") return <img key={i} src={b.src} alt={b.alt} loading="lazy" className="w-full rounded-2xl my-2" data-testid="blog-content-image" />;
      if (b.type === "table") return (
        <div key={i} className="overflow-x-auto my-6 rounded-xl border border-slate-200" data-testid="blog-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                {b.header.map((h, k) => (
                  <th key={k} className="text-left font-bold text-slate-800 px-4 py-3 border-b border-slate-200 align-top">{inline(h, `${i}-h-${k}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, r) => (
                <tr key={r} className="odd:bg-white even:bg-slate-50/40">
                  {row.map((cell, c) => (
                    <td key={c} className={`px-4 py-3 border-b border-slate-100 align-top ${c === 0 ? "font-semibold text-slate-800" : "text-slate-600"} leading-relaxed`}>
                      {inline(cell, `${i}-${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      if (b.type === "h2") return <h2 key={i} className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 mt-10 mb-3 pb-2 border-b-2 border-emerald-200/70 leading-tight">{inline(b.text, i)}</h2>;
      if (b.type === "h3") return <h3 key={i} className="font-display font-bold text-xl text-slate-900 mt-6 mb-2 leading-snug">{inline(b.text, i)}</h3>;
      if (b.type === "ul") return (
        <ul key={i} className="list-disc pl-5 space-y-1.5 text-slate-600 leading-relaxed">
          {b.items.map((it, j) => <li key={j}>{inline(it, `${i}-${j}`)}</li>)}
        </ul>
      );
      return <p key={i} className="text-slate-600 leading-relaxed">{inline(b.text, i)}</p>;
    })}
  </div>
);
